"use client"

import { useMemo, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { Typography } from "@/components/typography"
import type { PostIndexSection } from "@/lib/posts"

/**
 * Search, cluster filter, and sectioned browse for the Knowledge Base index.
 *
 * The only client component on the page, and deliberately dependency-free:
 * plain state, a substring match, and array maths. With a registry this size
 * there is nothing to gain from a search library and a lot to lose from the
 * bundle.
 *
 * Crawlability: cards are never removed from the DOM. Filtering toggles the
 * `hidden` attribute instead of slicing the array, so every post link is in
 * the server-rendered HTML on first paint — a crawler that ignores the JS
 * still sees the full set. Hidden cards also skip image fetches, because a
 * lazy image inside a `hidden` element never enters the viewport.
 */

export type BrowserPost = {
  slug: string
  href: string
  title: string
  description: string
  typeLabel: string
  section: PostIndexSection
  cluster: string
  clusterLabel: string
  dateLabel: string
  datePublished: string
  image: { src: string; alt: string }
  /** Extra text folded into the search haystack (never rendered). */
  keywords: string
}

const SECTIONS: {
  key: PostIndexSection
  title: string
  description: string
}[] = [
  {
    key: "answers",
    title: "Answers",
    description:
      "Direct answers to the questions startups and agencies actually ask.",
  },
  {
    key: "guides",
    title: "Guides",
    description:
      "Step-by-step guides for migrations, performance fixes, and the systems work clients need to understand.",
  },
  {
    key: "build-notes",
    title: "Build notes",
    description:
      "Implementation notes from shipped work — trade-offs, gotchas, and the details a roundup skips.",
  },
]

function PostCard({ post }: { post: BrowserPost }) {
  return (
    <Link
      href={post.href}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-tk-slate/10 bg-tk-linen transition-colors hover:border-tk-teal"
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-tk-slate/5">
        <Image
          src={post.image.src}
          alt={post.image.alt}
          fill
          sizes="(max-width: 640px) 100vw, 33vw"
          loading="lazy"
          className="object-cover"
        />
      </div>
      <div className="flex flex-1 flex-col p-6">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-ui text-xs font-semibold uppercase tracking-[0.22em]">
          <span className="text-tk-teal">{post.typeLabel}</span>
          <span aria-hidden="true" className="text-tk-slate/70">
            ·
          </span>
          <span className="normal-case tracking-normal text-tk-slate/70">
            {post.dateLabel}
          </span>
        </p>
        <h2 className="tk-h3 mt-3">{post.title}</h2>
        <p className="mt-2 font-body text-sm leading-relaxed text-tk-slate/80">
          {post.description}
        </p>
        <p className="mt-4 font-ui text-xs font-semibold uppercase tracking-[0.22em] text-tk-slate/70">
          {post.clusterLabel}
        </p>
      </div>
    </Link>
  )
}

function FeaturedGuideCard({ post }: { post: BrowserPost }) {
  return (
    <Link
      href={post.href}
      className="group grid overflow-hidden rounded-2xl border border-tk-slate/10 bg-tk-linen transition-colors hover:border-tk-teal sm:grid-cols-2"
    >
      <div className="relative aspect-[16/9] min-h-[200px] overflow-hidden bg-tk-slate/5 sm:aspect-auto sm:h-full sm:min-h-[280px]">
        <Image
          src={post.image.src}
          alt={post.image.alt}
          fill
          sizes="(max-width: 640px) 100vw, 50vw"
          loading="lazy"
          className="object-cover"
        />
      </div>
      <div className="flex flex-col justify-center p-6 sm:p-8 lg:p-10">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-ui text-xs font-semibold uppercase tracking-[0.22em]">
          <span className="text-tk-teal">{post.typeLabel}</span>
          <span aria-hidden="true" className="text-tk-slate/70">
            ·
          </span>
          <span className="normal-case tracking-normal text-tk-slate/70">
            {post.dateLabel}
          </span>
        </p>
        <h2 className="tk-h2 mt-4">{post.title}</h2>
        <p className="mt-3 font-body leading-relaxed text-tk-slate/80">{post.description}</p>
        <p className="mt-6 font-ui text-xs font-semibold uppercase tracking-[0.22em] text-tk-slate/70">
          {post.clusterLabel}
        </p>
      </div>
    </Link>
  )
}

function SectionHeader({
  id,
  title,
  description,
}: {
  id: string
  title: string
  description: string
}) {
  return (
    <div className="mb-8 max-w-3xl space-y-3">
      <Typography variant="h2" as="h2" id={id}>
        {title}
      </Typography>
      <Typography variant="body" className="text-tk-slate/80">
        {description}
      </Typography>
    </div>
  )
}

export function PostBrowser({
  posts,
  clusters,
}: {
  posts: BrowserPost[]
  clusters: { key: string; label: string }[]
}) {
  const [query, setQuery] = useState("")
  const [cluster, setCluster] = useState<string>("all")

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    return posts.filter((post) => {
      if (cluster !== "all" && post.cluster !== cluster) return false
      if (!q) return true
      return (
        `${post.title} ${post.description} ${post.typeLabel} ${post.clusterLabel} ${post.keywords}`
          .toLowerCase()
          .includes(q)
      )
    })
  }, [posts, query, cluster])

  const filtered = query.trim() !== "" || cluster !== "all"

  const allGuides = posts.filter((post) => post.section === "guides")
  const featureGuides = allGuides.length <= 2
  const guidesSection = SECTIONS.find((section) => section.key === "guides")!
  const answersSection = SECTIONS.find((section) => section.key === "answers")!
  const buildNotesSection = SECTIONS.find((section) => section.key === "build-notes")!

  const matchedGuides = matches.filter((post) => post.section === "guides")
  const matchedAnswers = matches.filter((post) => post.section === "answers")
  const matchedBuildNotes = matches.filter((post) => post.section === "build-notes")
  const showFeaturedGuides =
    featureGuides && allGuides.length > 0 && matchedGuides.length > 0

  return (
    <div className="space-y-12">
      <div className="space-y-4">
        <form role="search" onSubmit={(event) => event.preventDefault()}>
          <label htmlFor="kb-search" className="sr-only">
            Search the knowledge base
          </label>
          <div className="relative">
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-tk-slate/70"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              id="kb-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search answers, guides, and notes"
              className="w-full rounded-full border border-tk-slate/20 bg-white/60 py-3 pl-11 pr-4 font-body text-sm text-tk-onyx placeholder:text-tk-slate/70 focus:border-tk-teal focus:outline-none focus:ring-2 focus:ring-tk-teal/30"
            />
          </div>
        </form>

        <div className="flex flex-wrap items-center gap-2">
          {[{ key: "all", label: "Everything" }, ...clusters].map((option) => {
            const active = cluster === option.key
            return (
              <button
                key={option.key}
                type="button"
                aria-pressed={active}
                onClick={() => setCluster(option.key)}
                className={`rounded-full border px-4 py-1.5 font-ui text-xs font-semibold transition-colors ${
                  active
                    ? "border-tk-teal bg-tk-teal text-tk-linen"
                    : "border-tk-slate/20 text-tk-slate hover:border-tk-teal hover:text-tk-onyx"
                }`}
              >
                {option.label}
              </button>
            )
          })}
        </div>

        <p aria-live="polite" className="font-body text-sm text-tk-slate/70">
          {matches.length === posts.length
            ? `${posts.length} entries`
            : `${matches.length} of ${posts.length} entries`}
        </p>
      </div>

      {matches.length === 0 && (
        <div className="rounded-2xl border border-tk-slate/10 bg-tk-linen p-8 text-center">
          <p className="font-body text-tk-slate/80">
            Nothing matches “{query}” yet.
          </p>
          <button
            type="button"
            onClick={() => {
              setQuery("")
              setCluster("all")
            }}
            className="mt-3 font-ui text-sm font-semibold text-tk-teal hover:underline"
          >
            Clear the search
          </button>
        </div>
      )}

      {showFeaturedGuides && (
        <section aria-labelledby="kb-guides">
          <SectionHeader
            id="kb-guides"
            title={guidesSection.title}
            description={guidesSection.description}
          />
          <ul className="space-y-6">
            {allGuides.map((post) => (
              <li
                key={post.slug}
                hidden={!matchedGuides.some((match) => match.slug === post.slug)}
              >
                <FeaturedGuideCard post={post} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {matchedAnswers.length > 0 && (
        <section aria-labelledby="kb-answers">
          <SectionHeader
            id="kb-answers"
            title={answersSection.title}
            description={answersSection.description}
          />
          <ul className="grid gap-6 sm:grid-cols-3">
            {posts
              .filter((post) => post.section === "answers")
              .map((post) => (
                <li
                  key={post.slug}
                  hidden={!matchedAnswers.some((match) => match.slug === post.slug)}
                >
                  <PostCard post={post} />
                </li>
              ))}
          </ul>
        </section>
      )}

      {!featureGuides && matchedGuides.length > 0 && (
        <section aria-labelledby="kb-guides">
          <SectionHeader
            id="kb-guides"
            title={guidesSection.title}
            description={guidesSection.description}
          />
          <ul className="grid gap-6 sm:grid-cols-3">
            {posts
              .filter((post) => post.section === "guides")
              .map((post) => (
                <li
                  key={post.slug}
                  hidden={!matchedGuides.some((match) => match.slug === post.slug)}
                >
                  <PostCard post={post} />
                </li>
              ))}
          </ul>
        </section>
      )}

      {matchedBuildNotes.length > 0 && (
        <section aria-labelledby="kb-build-notes">
          <SectionHeader
            id="kb-build-notes"
            title={buildNotesSection.title}
            description={buildNotesSection.description}
          />
          <ul className="grid gap-6 sm:grid-cols-3">
            {posts
              .filter((post) => post.section === "build-notes")
              .map((post) => (
                <li
                  key={post.slug}
                  hidden={!matchedBuildNotes.some((match) => match.slug === post.slug)}
                >
                  <PostCard post={post} />
                </li>
              ))}
          </ul>
        </section>
      )}

      {filtered && matches.length > 0 && (
        <p className="text-center">
          <button
            type="button"
            onClick={() => {
              setQuery("")
              setCluster("all")
            }}
            className="font-ui text-xs font-semibold uppercase tracking-[0.22em] text-tk-teal hover:underline"
          >
            Clear filters
          </button>
        </p>
      )}
    </div>
  )
}
